'use client'
import Modal from '@crystal-eyes/components/elements/Modal'
import styles from './EditProfileModal.module.scss'
import { DiscType } from '@crystal-eyes/types'
import useEditProfile from '../hooks/useEditProfile'
import CircumplexChart from '@crystal-eyes/components/views/profiles/CircumplexChart'
import { useEffect, useState } from 'react'
import useIsOwnProfile from '@crystal-eyes/hooks/profiles/context/useIsOwnProfile'
import usePersonalInfo from '@crystal-eyes/hooks/profiles/usePersonalInfo'
import SectionCard from './SectionCard'
import usePersonality from '@crystal-eyes/hooks/profiles/usePersonality'
import Icon, { Icons } from '@crystal-eyes/components/elements/Icon/Icon'

interface Identity {
  id: string
}

type Props = { identity?: Identity }

const DEFAULT_INTENSITY = 75

export default function EditProfileModal({ identity }: Props) {
  const { data: isOwnProfile } = useIsOwnProfile({ identity })
  const { data: personalInfo } = usePersonalInfo({ identity })
  const { data: personality } = usePersonality({ identity })

  const {
    editProfileState: { state: editState, setState: setEditState },
    setMyPersonality,
    createUnownedProfile,
  } = useEditProfile({ identity })

  const [firstName, setFirstName] = useState<string>(
    personalInfo?.firstName || '',
  )
  const [lastName, setLastName] = useState<string>(personalInfo?.lastName || '')
  const [gender, setGender] = useState<string>(personalInfo?.gender || '')

  const [selectedDiscType, setSelectedDiscType] = useState<DiscType | null>(
    null,
  )

  const [selectedIntensity, setSelectedIntensity] =
    useState<number>(DEFAULT_INTENSITY)

  useEffect(() => {
    setSelectedDiscType(null)
    setSelectedIntensity(DEFAULT_INTENSITY)
  }, [identity?.id, setSelectedDiscType, setSelectedIntensity])

  useEffect(() => {
    if (!personalInfo) return
    setFirstName(personalInfo.firstName || '')
    setLastName(personalInfo.lastName || '')
    setGender(personalInfo.gender || 'prefer_not_say')
  }, [personalInfo])

  useEffect(() => {
    if (!personality) return
    if (personality.discType) setSelectedDiscType(personality.discType)
  }, [personality])

  if (editState === 'none') return null
  if (!identity) return null

  const doSubmit = () => {
    if (isOwnProfile) {
      setMyPersonality(selectedDiscType!, selectedIntensity).then(() => {
        setEditState('none')
        window.location.reload()
      })
    } else {
      createUnownedProfile({
        firstName,
        lastName,
        gender,
        discType: selectedDiscType!,
        intensity: selectedIntensity,
      }).then(() => {
        setEditState('none')
        window.location.reload()
      })
    }
  }

  return (
    <Modal
      className={styles.editProfileModal}
      close={() => {
        setEditState('none')
      }}
    >
      <SectionCard
        cardId="edit-profile"
        title="Edit Profile"
        icon={Icons.Edit}
        className={`CE disc-type ${personality?.discType || ''} card`}
      >
        {!isOwnProfile && (
          <div className="profile-settings">
            <div className="setting">
              <label htmlFor="first-name">
                First Name
                <input
                  type="text"
                  name="first-name"
                  value={firstName}
                  onChange={evt => setFirstName(evt.target.value)}
                />
              </label>
            </div>

            <div className="setting">
              <label htmlFor="last-name">
                Last Name
                <input
                  type="text"
                  name="first-name"
                  value={lastName}
                  onChange={evt => setLastName(evt.target.value)}
                />
              </label>
            </div>

            <div className="setting">
              <label htmlFor="gender">
                Gender
                <select
                  value={gender}
                  onChange={evt => setGender(evt.target.value)}
                >
                  <option value="prefer_not_say">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </label>
            </div>
          </div>
        )}

        <div className="type-and-intensity">
          {isOwnProfile && (
            <p>
              Set your DISC type manually. This will overwrite your current
              profile.
            </p>
          )}

          {!isOwnProfile && (
            <p>
              If you know {personalInfo?.firstName || 'this person'}, you can
              override the prediction with a custom personality type. Only you
              will be able to see this profile.
            </p>
          )}

          <section className="disc-type">
            <h3>DISC Type</h3>

            <CircumplexChart
              identities={[identity]}
              className="disc-wheel"
              highlightedTypes={
                selectedDiscType ? [selectedDiscType] : undefined
              }
              onClick={discType => {
                if (discType?.discType) {
                  setSelectedDiscType(discType.discType)
                }
              }}
            />
          </section>

          <section className="intensity">
            <h3>Intensity</h3>
            <input
              type="range"
              min="1"
              max="100"
              value={selectedIntensity}
              onChange={evt => setSelectedIntensity(parseInt(evt.target.value))}
            />

            <div className="explanations">
              <div className="CE tooltip explanation">
                Low Intensity{' '}
                <Icon icon={Icons.Info} className={styles.icon}></Icon>
                <div className="tooltip-content">
                  If someone has lower intensity, they are more likely to
                  exhibit traits that are a mix of several personality types.
                  <br />
                  <br />
                  If you’re not sure, you can leave intensity at its default
                  value of {DEFAULT_INTENSITY}.
                </div>
              </div>

              <div className="intensity-value">{selectedIntensity}%</div>

              <div className="CE tooltip explanation">
                High Intensity{' '}
                <Icon icon={Icons.Info} className={styles.icon}></Icon>
                <div className="tooltip-content">
                  If someone has higher intensity, they are more likely to
                  exhibit the traits associated with one specific personality
                  type.
                  <br />
                  <br />
                  If you’re not sure, you can leave intensity at its default
                  value of {DEFAULT_INTENSITY}.
                </div>
              </div>
            </div>
          </section>

          <div className="buttons">
            <button
              className="CE cancel-button"
              onClick={() => {
                setEditState('none')
              }}
            >
              Cancel
            </button>
            <button
              disabled={!selectedDiscType}
              className="CE action-button"
              onClick={doSubmit}
            >
              Save Profile
            </button>
          </div>
        </div>
      </SectionCard>
    </Modal>
  )
}
